/**
 * The regent's live screen, ported from `legacy/frontend/src/screens/RegentScreen.tsx` at legacy
 * commit 2953e3f, a file `d5aa0fa` does not change. F-007 built the code plate one act at a time
 * as the singer's screen was; F-012 ported the rest of the file in legacy's order — the rows
 * branch of the poll and its haptic, the fold's landing, the counter, the roster with the row's
 * handlers, the pinned panel with its menu and the undo, and the host's two props. The marking
 * by hand is F-010's, folded into this slice by `FND-179`; the pinned panel is drawn whole by
 * `FND-191`; `toggleExcuse`, F-011's, is ported here by `FND-203` (A). F-013 ported the close
 * question, `closeSession`, `unmarked`, `plural` and `onClosed`.
 *
 * **The head is written here and in no helper the singer's screen could share.** «ИДЁТ · »
 * and «ИДЁТ С » (`MarkScreen.tsx:449`) are two literals in two files, and `FND-131` was
 * decided with that clause: no component, hook or helper imported by both may render
 * `.live-line`. AC-130.
 *
 * **The QR is drawn here** — the `<img>` (`:541-553`), the `qr` state (`:90`), `canvasToken`
 * (`:146`) and the `QRCode.toDataURL` effect (`:194-198`), keyed on `qr_token`. `FND-147`,
 * decided `FIX`: the expand sign `span.code-hint` sits **inside** `span.qr` (`:543-551`), so a
 * plate with the slot left empty would have no place to put the sign the fold owes. F-008a
 * keeps the `a_` deep link that the payload points at.
 *
 * `SessionCodeResponse` is the name the contract gives legacy's `SessionCode`
 * (`packages/contracts/src/attendance.ts:80`), which is the one place a server shape may be
 * declared — ADR-0011.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';

import type {
  AttendanceStatus,
  LiveRow,
  MyMark,
  SessionCodeResponse,
  VoicePart,
} from '@contracts/index';

import { ApiError, api } from '../api';
import {
  arrivalInputValue,
  arrivalIso,
  arrivalProblem,
  formatTime,
  hasLeeway,
  plural,
  voicePartLetter,
  voicePartName,
  words,
} from '../format';
import { useFold } from '../fold';
import { useMeasuredBar } from '../measure';
import { DotsIcon, ExpandIcon } from '../icons';
import { usePoll } from '../liveness';
import { haptic } from '../telegram';
import Ask from './Ask';
import AttendanceRow from './AttendanceRow';
import { useFlip } from './flip';
import Unfold from './Unfold';

const POLL_INTERVAL_MS = 2000;
const ROTATION_SECONDS = 30;

/**
 * Who is marked now and was not at the previous answer.
 *
 * Nobody, when there is no previous answer: opening the screen is not an event,
 * and lighting up everybody already marked would say that fourteen people
 * arrived at once. Somebody the previous answer did not carry at all is not an
 * arrival either - `get` gives undefined there, and only a remembered `null`
 * counts.
 */
function arrivalsSince(before: Map<number, AttendanceStatus | null> | null, rows: LiveRow[]) {
  if (before === null) return [];
  return rows
    .filter((row) => row.status !== null && before.get(row.member_id) === null)
    .map((row) => row.member_id);
}

export default function RegentScreen({
  myMark,
  onMarkedSelf,
  onSessionsChanged,
  onClosed,
  onCancelled,
}: {
  /** The regent's own mark in the running rehearsal, if there is one. */
  myMark: MyMark | null;
  onMarkedSelf: () => void;
  /**
   * A rehearsal was opened, closed or cancelled. The list behind this screen is
   * a different request and was made before any of that.
   */
  onSessionsChanged: () => void;
  /**
   * It is over, and this is the same evening's own screen: what it came to and
   * the roster under it are one scroll away rather than one tab away. The card
   * that used to say so here, with «Проверить состав» leading to the other tab,
   * is what `reviewSession` in `App` existed for - both are gone since
   * 2026-08-22.
   */
  onClosed: (sessionId: string) => void;
  /** Opening it was a mistake and has been undone: there is nothing to show. */
  onCancelled: () => void;
}) {
  const [code, setCode] = useState<SessionCodeResponse | null>(null);
  // Until the server's first answer the state is unknown. Without this flag the
  // regent sees "no rehearsal open" with an Open button on top of one that is.
  const [loaded, setLoaded] = useState(false);
  // Two kinds of bad news, and they must not overwrite each other. The poll
  // owns the first: it fires every two seconds and clears itself the moment the
  // connection comes back. The second belongs to whatever the regent just did,
  // and a poll that succeeds a second later says nothing about it - it used to
  // wipe the answer off the screen before it could be read.
  const [failure, setFailure] = useState<string | null>(null);
  const [editFailure, setEditFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  /* One state for both irreversible endings rather than a flag each: both are
     asked from the same sheet, and two sheets open at once is not a state. */
  const [confirming, setConfirming] = useState<'close' | 'cancel' | null>(null);
  // Whether the code is in its strip. The strip is the state the screen opens
  // in since 2026-08-21: a phone is held as often as it is stood up, and the
  // plate is one tap away, while what the regent needs all the time is the
  // count and the names. Kept in this component so that the two-second poll
  // redrawing the digits under it cannot reset it.
  const [codeFolded, setCodeFolded] = useState(true);
  /* The plate's travel, which is a number and not this boolean: a finger can
     stop it anywhere. The boolean is what the rest of the screen hangs on, so
     it is set when the fold comes to rest and not on every frame of it - the
     counter and the roster would otherwise appear and go with the pull. The
     panel's block has no padding of its own when it is open, so the plate is
     the width of the screen's content: the inset is 0. */
  const fold = useFold(0, (open) => foldCode(!open));
  /* C-016 (FND-1611): the counter gives up its room on the fold's own run rather than in
     its first frame. Unmounted, it took its 73px and the pane's 12px gap with it, and the
     block leapt 85px before the plate began to move - up on the way open, down against the
     motion on the way back. Measured, not assumed: the counter's height follows its
     contents and the webfont, and the gap is the pane's. */
  const [counterBox, setCounterBox] = useState({ height: 0, gap: 0 });
  const counterWatch = useRef<ResizeObserver | null>(null);
  const holdCounter = useCallback((node: HTMLDivElement | null) => {
    counterWatch.current?.disconnect();
    counterWatch.current = null;
    if (node === null) return;
    const measure = (): void => {
      const height = node.getBoundingClientRect().height;
      const pane = node.parentElement?.parentElement;
      const gap = pane ? parseFloat(getComputedStyle(pane).rowGap) || 0 : 0;
      setCounterBox((was) => (was.height === height && was.gap === gap ? was : { height, gap }));
    };
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(node);
      counterWatch.current = observer;
    }
    measure();
  }, []);
  useEffect(() => () => counterWatch.current?.disconnect(), []);
  // Show only the people nobody has marked yet. Follows the fold - a folded
  // code is the regent working down the list of who is missing - but stays
  // overridable, because that is a question he also asks with the plate up.
  const [onlyMissing, setOnlyMissing] = useState(true);
  /**
   * The panel measures itself, and until 2026-09-07 it declared 67px in the
   * stylesheet instead — one 46px button and its padding. Open its menu and it
   * is 125: «Открыл по ошибке» is a second button and the sentence that stands
   * in its place is two lines, so 58px of the screen sat behind the panel and
   * the hint above it was cut through the middle. The owner reported it as
   * «слиплось, отступа нет».
   */
  const panel = useMeasuredBar('--panel');

  // The rest of the panel's actions, behind the «…» beside «Завершить». They
  // are two taps deep because both are rare and one of them is irreversible.
  const [menuOpen, setMenuOpen] = useState(false);
  // One voice part to show in the roster, or undefined for the whole choir.
  // Set by tapping a column of the counter.
  const [partFilter, setPartFilter] = useState<VoicePart | null | undefined>(undefined);
  /* Who has been given a verdict since the roster was last set up this way.
     «кого нет» keeps the rows whose status is null, so a mark that landed used
     to delete the row the finger was still on and pull the rest of the list up
     past it - on the regent's main working path, and against the rule that a
     row only ever changes its colour and its words. A row that has just been
     marked therefore stays where it is, carrying the verdict it was given,
     until the filter itself is touched: that is the next moment the regent is
     reading the list rather than working down it. */
  const [markedHere, setMarkedHere] = useState<ReadonlySet<number>>(() => new Set());
  // Who was marked what on the previous answer, so that an arrival can be told
  // from a state. A ref: it is compared, never rendered.
  const previousStatuses = useRef<Map<number, AttendanceStatus | null> | null>(null);
  // The expanded row of the live list. Kept apart from code so that polling
  // every two seconds does not close the toggles under the regent's finger.
  const [editing, setEditing] = useState<number | null>(null);
  // Arrival times being typed in, by member. Kept out of the roster for the same
  // reason as the expanded row: the poll would wipe a half-typed one every two
  // seconds. Absent means "not touched", and the row's own time shows instead.
  const [arrivalDrafts, setArrivalDrafts] = useState<Record<number, string>>({});
  const canvasToken = useRef<string | null>(null);
  // The roster we already hold, with the fingerprint the server gave it. Sent
  // back on every poll so the server can skip re-sending the same names: the
  // list is 95 % of the response and changes only when somebody checks in. A
  // ref and not state — it is never rendered, and putting it in state would
  // rebuild the poll callback and restart the interval on every answer.
  const heldRoster = useRef<{ version: string; rows: SessionCodeResponse['rows'] } | null>(null);

  const poll = useCallback(async () => {
    try {
      const fresh = await api.currentCode(heldRoster.current?.version);
      if (fresh.rows !== null) {
        const arrived = arrivalsSince(previousStatuses.current, fresh.rows);
        previousStatuses.current = new Map(fresh.rows.map((row) => [row.member_id, row.status]));
        // The quiet tick in the hand for an arrival. It used to be the second
        // signal beside a segment that swelled on its way to green; the «Пульт»
        // revision keeps the haptic and drops the movement, so in the hand this
        // is now the only notice that somebody came.
        if (arrived.length > 0) haptic('light');
        heldRoster.current = { version: fresh.rows_version, rows: fresh.rows };
        setCode(fresh);
      } else {
        // Unchanged, so the server left the roster out. Put back the one it
        // left out: null here means "keep yours", never "the choir is empty".
        setCode({ ...fresh, rows: heldRoster.current?.rows ?? null });
      }
      setFailure(null);
    } catch (error) {
      if (error instanceof ApiError && error.failure?.error === 'no_open_session') {
        setCode(null);
        // Drop the roster with the rehearsal: the next one starts from the
        // server's answer rather than from what the previous one left behind.
        heldRoster.current = null;
        previousStatuses.current = null;
        setFailure(null);
        return;
      }
      setFailure(error instanceof ApiError ? error.message : 'Связь потеряна');
    } finally {
      setLoaded(true);
    }
  }, []);

  // Poll every two seconds: one request refreshes both the code and the list.
  // SSE buys nothing at fifty members. The hook stands still while the webview
  // is off screen and asks at once when it comes back.
  usePoll(poll, POLL_INTERVAL_MS);

  useEffect(() => {
    if (code === null || canvasToken.current === code.qr_token) return;
    canvasToken.current = code.qr_token;
    // `FND-149`, `FIX`, human 2026-09-12: legacy has no second argument here and no error
    // tracker either. This tree has one, and an unhandled rejection would carry
    // `qr_payload` — the check-in link — into it (`app/src/tracking.ts:183-185`).
    void QRCode.toDataURL(code.qr_payload, { margin: 1, width: 320 }).then(setQr, () => {});
  }, [code]);

  /**
   * Fold the code into its strip, or unfold it back to the plate.
   *
   * The list of who is missing follows, because that is the pair of states the
   * frames draw: plate and everybody, strip and «кого нет». Either half can
   * then be set on its own.
   */
  function foldCode(folded: boolean): void {
    setCodeFolded(folded);
    setOnlyMissing(folded);
    setMarkedHere(new Set());
  }

  /** «все ▾»: the whole choir again, both filters off. */
  function releaseFilters(): void {
    flipRoster();
    setOnlyMissing(false);
    setPartFilter(undefined);
    setMarkedHere(new Set());
  }

  /**
   * A tap on a counter column: show that part in the roster.
   *
   * The same tap on the part already shown releases the filter rather than
   * doing nothing — the gesture has to be able to undo itself.
   */
  function tapPart(part: VoicePart | null): void {
    flipRoster();
    setMarkedHere(new Set());
    if (partFilter === part) {
      setPartFilter(undefined);
      return;
    }
    setPartFilter(part);
  }

  /**
   * Check the regent in with the code he is already holding.
   *
   * The one case it exists for is the second regent, arriving at a rehearsal
   * somebody else opened: he cannot scan a QR that is on his own screen, and the
   * digits are printed in front of him. Submitting them rather than writing a
   * mark directly keeps the server's arithmetic honest - on time or late is
   * decided the same way as for anybody who types the code.
   */
  async function markSelf(): Promise<void> {
    if (code === null) return;
    setBusy(true);
    setEditFailure(null);
    try {
      await api.markByCode(code.numeric_code);
      await poll();
      onMarkedSelf();
    } catch (error) {
      setEditFailure(error instanceof ApiError ? error.message : 'Не удалось отметиться');
    } finally {
      setBusy(false);
    }
  }

  async function closeSession(): Promise<void> {
    // Read before the request: closing is what takes the roster away, and the
    // id of what was running is what the screen after this one is about.
    const id = code?.session.session_id ?? null;
    setBusy(true);
    try {
      await api.closeSession();
      setCode(null);
      heldRoster.current = null;
      previousStatuses.current = null;
      setConfirming(null);
      setMenuOpen(false);
      onSessionsChanged();
      if (id !== null) onClosed(id);
    } catch (error) {
      setEditFailure(
        error instanceof ApiError
          ? error.message
          : `Не удалось закрыть ${words(code?.session.kind ?? 'rehearsal').accusative}`,
      );
    } finally {
      setBusy(false);
    }
  }

  async function cancelSession(): Promise<void> {
    setBusy(true);
    try {
      await api.cancelSession();
      setCode(null);
      heldRoster.current = null;
      setConfirming(null);
      setMenuOpen(false);
      onSessionsChanged();
      onCancelled();
    } catch (error) {
      setEditFailure(
        error instanceof ApiError
          ? error.message
          : `Не удалось отменить ${words(code?.session.kind ?? 'rehearsal').accusative}`,
      );
    } finally {
      setBusy(false);
    }
  }

  /** Edit, then poll at once: the panel has to show the new status. */
  async function edit(action: () => Promise<unknown>, fallback: string): Promise<boolean> {
    setBusy(true);
    setEditFailure(null);
    try {
      await action();
      await poll();
      return true;
    } catch (error) {
      setEditFailure(error instanceof ApiError ? error.message : fallback);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(row: LiveRow, status: AttendanceStatus): Promise<void> {
    if (code === null) return;
    // The haptic confirms the choice, not the round trip: the finger just
    // picked a segment, and the answer will redraw the row on its own.
    haptic('selection');
    // Held before the request rather than after it: the row has to survive the
    // answer that gives it a status, and the answer is what re-filters the list.
    setMarkedHere((held) => new Set(held).add(row.member_id));
    const sessionId = code.session.session_id;
    await edit(
      () => api.markManually({ sessionId, memberId: row.member_id, status }),
      'Не удалось изменить отметку',
    );
    // Any other status rewrote the row's time on the server, so a time typed
    // into the old one and never saved is no longer about anything.
    if (status !== 'late') dropArrivalDraft(row.member_id);
  }

  function dropArrivalDraft(memberId: number): void {
    setArrivalDrafts((current) => {
      const next = { ...current };
      delete next[memberId];
      return next;
    });
  }

  function arrivalOf(row: LiveRow): string {
    return arrivalDrafts[row.member_id] ?? arrivalInputValue(row);
  }

  /** Keeps the status and only adds the minutes; an empty field saves nothing. */
  async function saveArrival(row: LiveRow): Promise<void> {
    if (code === null) return;
    const sessionId = code.session.session_id;
    const problem = arrivalProblem(code.session.effective_start, arrivalOf(row));
    if (problem !== null) {
      setEditFailure(problem);
      return;
    }
    const markedAt = arrivalIso(code.session.effective_start, arrivalOf(row));
    if (markedAt === null) return;
    const saved = await edit(
      () => api.markManually({ sessionId, memberId: row.member_id, status: 'late', markedAt }),
      'Не удалось сохранить время',
    );
    if (!saved) return;
    dropArrivalDraft(row.member_id);
  }

  async function toggleExcuse(row: LiveRow): Promise<void> {
    if (code === null) return;
    const sessionId = code.session.session_id;
    await edit(
      () => api.setExcused(sessionId, row.member_id, !row.is_excused),
      'Не удалось изменить уважительность',
    );
  }

  // Above the two early returns since C-023 batch 3 (`FND-1618`), so the roster's rows can be
  // handed to `useFlip` — a hook, which no return may come before. `code` may be null here; the
  // list is then empty, and nothing below the returns reads it.
  const rows = code?.rows ?? [];

  /* What the roster is showing, and what its label calls it. Two filters that
     stack: a voice part picked from the counter, and «кого нет», which follows
     the code's fold. The label names whichever is narrower, so the heading is
     never wider than the list under it. */
  const inPart = rows.filter((row) => partFilter === undefined || row.voice_part === partFilter);
  const missing = (row: LiveRow) => !onlyMissing || row.status === null;
  const visible = inPart.filter((row) => missing(row) || markedHere.has(row.member_id));

  // C-023 batch 3 (`FND-1618`): a tap that changes which rows the roster shows moves the rows
  // that stay and fades in the ones that come, rather than redrawing the list in one frame.
  const rosterList = useRef<HTMLUListElement>(null);
  const flipRoster = useFlip(
    rosterList,
    visible.map((row) => row.member_id),
  );

  // The choir's name is not repeated here: it stands in the header above every
  // screen now.
  if (!loaded) {
    return <p className="hint">Смотрю, идёт ли что-нибудь…</p>;
  }

  // The gathering has ended under this screen — closed, or the opening undone.
  // Both hand the screen over: closing goes to the same evening's own summary
  // and roster, cancelling to the list. What is drawn here is the frame or two
  // in between, and the refusal when the ending itself was refused.
  if (code === null) {
    return (
      <>
        <Unfold open={editFailure !== null}>
          <p className="failure">{editFailure}</p>
        </Unfold>
        <Unfold open={failure !== null}>
          <p className="failure">{failure}</p>
        </Unfold>
      </>
    );
  }

  const progress = (code.seconds_until_rotation / ROTATION_SECONDS) * 100;
  // Null should not reach here: it only comes back for a version we sent, and
  // we only send one we hold. If it ever does, an empty list for one tick is
  // the harmless answer — the next poll carries no version and gets the whole
  // roster back.
  // What the filter says, which is not the same as what is on screen: a row
  // held after being marked is no longer one of «кого нет», and the figure
  // beside the label answers for the filter rather than for the list.
  const matching = inPart.filter(missing).length;
  const rosterFiltered = onlyMissing || partFilter !== undefined;
  const rosterLabel =
    partFilter !== undefined
      ? voicePartName(partFilter).toUpperCase()
      : onlyMissing
        ? 'КОГО НЕТ'
        : 'УЧАСТНИКИ';
  /* Who closing would write an absence for: nobody has touched their row at
     all. Not active_member_count - present_count, which also counts everybody
     the regent has already marked absent by hand - they have their absence
     already, and closing changes nothing for them. */
  const unmarked = rows.filter((row) => row.status === null).length;

  const counter = (
    <div className="counter">
      {/* Monospaced, because this figure changes under the regent's eye as
          people arrive and a proportional «1» moving to «2» shifts everything
          after it. */}
      <p className="counter-figure">
        <span className="figure figure--answer figure--accent">{code.present_count}</span>
        <span className="figure-of">/{code.active_member_count}</span>
        {/* Both halves of the same fact, because the one the regent acts on is
            the second: «12 на месте» is the room filling up, «нет 28» is the
            length of the list he is about to work down. It replaced the
            «не хватает: сопрано 8 · альты 7 …» line under the counter, which
            said what the columns beside it already said and held 2.9em of
            height whether it had anything to say or not. «на месте» rather
            than «пришло», which is the past tense of a room that is still
            filling and the only tensed word left in the counter. */}
        <span className="counter-word">
          на месте · нет {code.active_member_count - code.present_count}
        </span>
      </p>
      {/* One column per voice part. The regent thinks in parts - «альты
          запаздывают» is an answer where «9 из 14» is only a count - and a tap
          on a column filters the roster down to it. */}
      <div className="counter-parts">
        {code.parts.map((part) => (
          <button
            key={part.voice_part ?? 'none'}
            className={
              partFilter === part.voice_part ? 'counter-part counter-part--picked' : 'counter-part'
            }
            data-press="fade"
            onClick={() => tapPart(part.voice_part)}
          >
            <span className="counter-part-value">
              {part.present}/{part.total}
            </span>
            <span className="counter-part-letter">{voicePartLetter(part.voice_part)}</span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {/* The state over the title rather than a badge beside it: «Пульт» opens
          every screen with one uppercase line saying what is happening, and the
          title under it saying what it is called.

          The grace rides in that line rather than taking a caption of its own.
          It is the moment nobody could see, and it belongs beside the start it
          is measured from. It drops out when there is nothing to say: a
          gathering opened well before its hour spends its whole grace on the
          waiting, so «вовремя до» lands on the start itself and the line would
          print the same minute twice. */}
      <div className="screen-head">
        <p className="label label--tone live-line">
          {/* C-016 (FND-1611): the ring stops while the poll is failing, so the dot does
              not say «now» about a screen that has stopped hearing the server. */}
          <span className="live-line-dot" data-stale={failure === null ? undefined : ''} />
          ИДЁТ · {formatTime(code.session.effective_start)}
          {hasLeeway(code.session) && <> · ВОВРЕМЯ ДО {formatTime(code.session.on_time_until)}</>}
        </p>
        <h1 className="screen-title">{code.session.title ?? words(code.session.kind).titled}</h1>
      </div>

      {/* Above the code since 2026-08-21. The count is what the regent needs
          all the time; the code is what he needs on demand, and under a plate
          that fills the screen the count went off the bottom of it the moment
          anybody looked at the digits.

          Gone entirely while the plate is up, along with the roster below it:
          that state is the phone standing on a music stand facing the choir,
          and what it is for is the code. Nobody reads a count from four metres,
          and the names of who is missing are not the choir's to read at all. */}
      {/* C-016 (FND-1611): held in place and folded away with the plate - see `counterBox`.
          `inert` keeps the folded-away columns out of reach and out of the tree, as their
          absence did. */}
      <div
        className="counter-hold"
        style={{
          height: codeFolded ? counterBox.height : 0,
          marginBottom: codeFolded ? 0 : -counterBox.gap,
          opacity: codeFolded ? 1 : 0,
          transition: fold.styles.slot.transition,
        }}
        inert={!codeFolded}
        aria-hidden={!codeFolded}
      >
        <div ref={holdCounter}>{counter}</div>
      </div>

      {/* One object in two sizes, and the finger moves between them: pull it,
          flick it, or tap it anywhere. The plate is what a phone on a stand
          shows a room; the strip is what stays on screen while the regent reads
          names off the roster and goes on saying the digits aloud. A code that
          blinked out and reappeared somewhere else would have to be found
          again, so the plate travels to the thumbnail's size and place rather
          than being swapped for it. `fold.ts` holds the arithmetic.

          Not a `<button>` any more: the handle inside it is one, and a button
          inside a button is not a thing.

          The strip is the state it opens in: a phone is held as often as it is
          stood up on a music stand, and only one of those two wants the poster. */}
      <div
        className="code-block"
        ref={fold.block}
        style={fold.styles.block}
        data-press="none"
        role="button"
        tabIndex={0}
        aria-expanded={fold.open}
        {...fold.grip}
      >
        {/* The plate's own heading. Expanded, the block is a composition
            rather than a bigger strip - what it is, the code, how long it
            lasts - and the heading is where the eye starts. The way back is no
            longer a word here: the whole block takes the tap. */}
        <span className="code-head" style={fold.styles.head}>
          <span ref={fold.head}>
            {/* The row is its own element so the 18 under it is inside what the
                fold clips. On the block above it, that margin would survive the
                height going to zero. */}
            <span className="code-head-row">
              <span className="label">{words(code.session.kind).titled} · код</span>
            </span>
          </span>
        </span>
        <span className="code-qr" style={fold.styles.slot}>
          {qr !== null && (
            <span className="qr" style={fold.styles.plate}>
              <img src={qr} alt="QR со ссылкой на отметку" />
              {/* The sign that this opens, until it has been opened once. */}
              {fold.hint && (
                <span className="code-hint" style={fold.styles.hint}>
                  <ExpandIcon size={14} />
                </span>
              )}
            </span>
          )}
        </span>
        {/* The digits and the seconds they have left, on one baseline and in
            that order in both states. The strip used to print «код · 24 с» on a
            line of its own and the plate «24 с» at the far end of the row: one
            fact, two elements, and neither of them beside the number it
            cancels. */}
        <span className="code-row" style={fold.styles.row}>
          <span className="code-line">
            <span className="code" style={fold.styles.code}>
              {code.numeric_code}
            </span>
            <span className="code-countdown">{code.seconds_until_rotation} с</span>
          </span>
        </span>
        <span className="code-tail" style={fold.styles.tail}>
          <span ref={fold.tail}>
            <span className="rotation">
              {/* C-016 (FND-1611): a transform over the poll's own two seconds, so the bar
                  runs without steps; keyed by the code, so a new code refills it at once
                  rather than running back up. */}
              <span
                key={code.numeric_code}
                className="rotation-bar"
                style={{ transform: `scaleX(${progress / 100})` }}
              />
            </span>
            {/* The frames dropped this line and the owner put it back: this is
                the one screen that has to stay lit, and a regent whose phone
                locks mid-rehearsal loses the code without being told why. One
                line and not three: it is a warning worth reading once. */}
            <span className="code-note">
              Код можно продиктовать. Отключи автоблокировку — иначе экран погаснет вместе с ним.
            </span>
          </span>
        </span>
        {/* The handle. On the bottom edge because that is the edge that moves,
            and a control rather than a pill: it takes the tap and it is what
            the accessibility services are given. */}
        <button
          className="code-handle"
          data-press="none"
          aria-label="Развернуть или свернуть код"
          onClick={fold.toggle}
        >
          <span />
        </button>
      </div>

      {/* Both open and close through their own height. Bad news lands above the
          counter, so a line arriving in one frame shoved the counter and the
          whole roster under it down by the height of a sentence nobody had
          asked for - and the poll's failure is not asked for by anybody at all.
          Going away in one frame did the same thing upwards, which is what
          `open` fixes: the sentence stays in the panel while it shrinks. */}
      <Unfold open={editFailure !== null && editing === null}>
        <p className="failure">{editFailure}</p>
      </Unfold>

      <Unfold open={failure !== null}>
        <div className="block">
          <p className="failure">{failure}</p>
          {/* Not «полоса снова пойдёт»: the rotation bar lives in the plate's
              tail, which the fold clips, and folded has been the default since
              2026-08-21. The seconds print in both states, and a failed poll
              leaves them standing. */}
          <p className="hint">
            Код на экране мог устареть: секунды стоят на месте. Продиктуй цифры, когда они снова
            пойдут.
          </p>
        </div>
      </Unfold>

      {/* Manual marking is not a fallback path: a phone died, someone came
          without a phone, somebody has to be corrected. Without it the regent
          goes back to the paper notebook. Which is why «здесь / нет» sit in the row
          itself rather than on a screen of their own. */}
      <div className={codeFolded ? 'block block--rows' : 'block block--rows block--hidden'}>
        <div className="split">
          <p className="label">
            {rosterLabel} · <span className="num">{matching}</span>
          </p>
          {/* One control, and only when there is a filter to release. The list
              itself no longer folds: the code is what folds on this screen now,
              and a roster hidden behind a word left the panel showing nothing
              but a strip and a count. */}
          <span className="label-actions">
            {rosterFiltered && (
              <button className="label-action" data-press="fade" onClick={releaseFilters}>
                все ▾
              </button>
            )}
          </span>
        </div>
        <ul className="live" ref={rosterList}>
          {visible.map((row) => (
            <AttendanceRow
              key={row.member_id}
              row={row}
              expanded={editing === row.member_id}
              busy={busy}
              onToggle={() => setEditing(editing === row.member_id ? null : row.member_id)}
              onSetStatus={(status) => void setStatus(row, status)}
              onToggleExcuse={() => void toggleExcuse(row)}
              arrivalTime={{
                value: arrivalOf(row),
                start: code.session.effective_start,
                // An empty field is not a way to say "unknown after all": the
                // server reads no time as "leave what is there", so there would
                // be nothing to save.
                dirty: arrivalOf(row) !== '' && arrivalOf(row) !== arrivalInputValue(row),
                onChange: (value) =>
                  setArrivalDrafts((current) => ({ ...current, [row.member_id]: value })),
                onSave: () => void saveArrival(row),
              }}
              /* A refusal earned inside the sheet is read inside the sheet.
                 `editFailure` also carries what «Отметить себя» and the panel's
                 menu were refused, and those are read where they happened - so
                 it goes to the row only while that row's sheet is the one up. */
              failure={editing === row.member_id ? editFailure : null}
            />
          ))}
        </ul>
      </div>

      {/* The regent who opened the rehearsal was marked by that act; the second
          one to arrive has no other way in, because a QR on his own screen is
          not scannable. Drawn only where there is a second regent to draw it
          for: a lone one is always marked by the opening, so for him the button
          and the paragraph explaining it described a situation that could not
          happen, permanently, at the top of the list he was working down.

          Where it is drawn, both of its states are the same height on purpose:
          the block used to appear for one and vanish for the other, so pressing
          «Отметить себя» took 106px out from under the finger that had just
          pressed it. */}
      {code.has_second_regent && (
        <div className="block">
          <div className="action-slot">
            {myMark === null ? (
              <button className="btn btn--outline" disabled={busy} onClick={() => void markSelf()}>
                Отметить себя
              </button>
            ) : (
              <p className="hint hint--center">Отметка есть</p>
            )}
          </div>
          <p className="hint">
            Открытие отмечает того, кто открыл. Второму регенту сканировать нечего — код и так на
            экране.
          </p>
        </div>
      )}

      {/* Pinned above the tab bar, and the reason is arithmetic: the roster is
          forty rows and «Закрыть спевку» stood after it, about 1500px down, so
          the one thing the regent would ever go looking for was the one thing
          he had to scroll for. Everything rarer than ending the rehearsal is
          behind the «…», and every question it asks is answered inside it -
          the panel is where the tap was. */}
      <div className="panel" ref={panel}>
        <div className="panel-row">
          {/* The one filled button on the screen. It is not what the regent
              came here to do - he came to show the code, and the code is not
              a button - so nothing competes for that weight. Filled and not
              red: the question it opens is where the danger colour lives,
              and until it has been asked nothing dangerous has happened. */}
          <button
            className="btn btn--primary"
            disabled={busy}
            onClick={() => setConfirming('close')}
          >
            Завершить {words(code.session.kind).accusative}
          </button>
          {/* The way out of the menu is the «…» that opened it, which is why
              the row stays put while the menu is under it - the arrangement a
              member's row has had all along. It used to be replaced by a pair
              «предложение и „Не надо“», and that «Не надо» was the last one in
              the app: refusing a question is closing what asked it. */}
          <button
            className="panel-more"
            aria-label="Ещё"
            aria-expanded={menuOpen}
            onClick={() => {
              setMenuOpen(!menuOpen);
              setEditFailure(null);
            }}
          >
            <DotsIcon size={20} />
          </button>
        </div>

        {/* Undoing is not closing, and the difference is the whole point:
            closing says the rehearsal happened and writes an absence for
            everyone who did not come. The way out is here only while nobody has
            come yet, which is also the only time the answer "this was a
            mistake" is true - and when it is too late, the sentence saying so
            stands in the same place, because a person who used it once looks
            there again and «ничего» is not an answer. */}
        <Unfold open={menuOpen}>
          <div className="menu menu--panel">
            {code.can_cancel ? (
              <button
                className="btn btn--outline"
                disabled={busy}
                onClick={() => setConfirming('cancel')}
              >
                Открыл по ошибке
              </button>
            ) : (
              /* A state, not a question. It used to be drawn as one, with
                 «Понятно» under it - which is asking somebody to answer a
                 message. The sentence takes the place of the button it is
                 about, and it takes the whole width: sharing a row with a
                 control squeezed it onto two lines. */
              <p className="hint">Отменить открытие нельзя: уже есть отметки.</p>
            )}
          </div>
        </Unfold>
      </div>

      {/* Closing is the one thing on this screen that writes on the whole choir
          at once and cannot be undone, and it used to happen on the first tap.
          So it is asked - from the sheet every irreversible thing in the app is
          asked from since 2026-08-21 - and the question carries the figure
          rather than a warning, because «шестерым запишется пропуск» stops a
          hand that «вы уверены?» does not. */}
      <Ask
        open={confirming === 'close'}
        title={`Завершить ${words(code.session.kind).accusative}?`}
        cost={
          <>
            Код перестанет работать
            {unmarked === 0 ? (
              ', и пропуск не запишется никому — отмечены все'
            ) : (
              <>
                , а тем, кто не отмечен, запишется пропуск — сейчас это{' '}
                <span className="num">{unmarked}</span>{' '}
                {plural(unmarked, ['человек', 'человека', 'человек'])}
              </>
            )}
            . Отметки потом можно поправить в «Прошедших».
          </>
        }
        /* «Завершить спевку» wrapped onto two lines when this was half a row of
           buttons; the heading has just named what closes, so the verb alone. */
        verb="Завершить"
        busy={busy}
        failure={editFailure}
        onConfirm={() => void closeSession()}
        onClose={() => setConfirming(null)}
      />

      <Ask
        open={confirming === 'cancel'}
        title={`Отменить ${words(code.session.kind).accusative}?`}
        cost={
          <>
            {words(code.session.kind).titled} исчезнет, как будто ничего не открывали: никто не
            получит пропуск, в посещаемости не появится. Если стояло в расписании, туда и вернётся.
          </>
        }
        verb={`Отменить ${words(code.session.kind).accusative}`}
        busy={busy}
        failure={editFailure}
        onConfirm={() => void cancelSession()}
        onClose={() => setConfirming(null)}
      />
    </>
  );
}
